import {
  trackOpticalFlowStep,
  type OpticalFlowFrame,
} from "../motion/motion-optical-flow";

/**
 * Canonical tracking coordinate space, obeyed by every value this engine stores
 * or returns: pixels of the clip's source media, origin at the top-left of the
 * decoded frame. Regions, keyframe positions and attachment offsets all live in
 * that space, and frame numbers are absolute source frame indices rather than
 * clip-relative ones. That is the same space the GPU "Object Tracking" job
 * reports bounding boxes in, so a local run and ingestTrackingData produce
 * interchangeable TrackingData. A TrackingFrameSource may hand back downscaled
 * pixels for speed; it declares the reduction in `scale` and the conversion is
 * confined to runTracking, so nothing outside this file sees analysis pixels.
 */
export interface Point {
  x: number;
  y: number;
}

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TrackingOptions {
  /** Frames per second of the source media. Maps frame indices to playback time. */
  frameRate: number;
  /** First source frame to sample. The region is taken as truth on this frame. */
  startFrame: number;
  /** Last source frame to sample, inclusive. */
  endFrame: number;
  /** Frames scoring below this land in lostFrames. Defaults to 0.7. */
  confidenceThreshold?: number;
  /** Radius in frames of the moving average applied to the path. 0 disables it. */
  smoothing?: number;
}

/**
 * Pixel supplier for a tracking run. The engine decodes nothing itself: the
 * caller owns the video element or demuxer and answers with the pixels of one
 * absolute source frame, or null when that frame cannot be decoded.
 */
export interface TrackingFrameSource {
  /**
   * Analysis pixels per source pixel in the frames getFrame returns, so 0.5
   * means half-resolution. Callers downscale because block matching costs
   * O(searchRadius^2 * blockRadius^2) per frame.
   */
  readonly scale: number;
  getFrame(frameIndex: number): Promise<OpticalFlowFrame | null>;
}

const DEFAULT_CONFIDENCE_THRESHOLD = 0.7;

/**
 * Match window and search window bounds, in analysis pixels. Both are capped
 * because block matching cost is quadratic in each: an unbounded region (a user
 * tracking half the frame) would otherwise stall the tab for minutes.
 */
const MAX_BLOCK_RADIUS = 24;
const MAX_SEARCH_RADIUS = 32;

export type TrackingJobStatus =
  | "pending"
  | "running"
  | "completed"
  | "cancelled"
  | "failed";

export interface TrackingJob {
  id: string;
  clipId: string;
  region: Rectangle;
  status: TrackingJobStatus;
  progress: number;
  options: TrackingOptions;
  startTime: number;
  endTime?: number;
  error?: string;
}

export interface TrackingKeyframe {
  frame: number;
  position: Point;
  scale?: number;
  rotation?: number;
}

export interface TrackingData {
  trackId: string;
  clipId: string;
  keyframes: TrackingKeyframe[];
  confidence: number[];
  lostFrames: number[];
  region: Rectangle;
  frameRate: number;
}

export interface TrackingAttachment {
  elementId: string;
  trackId: string;
  offset: Point;
  applyScale: boolean;
  applyRotation: boolean;
}

export type TrackingProgressCallback = (progress: number) => void;

export type TrackingLostCallback = (frameIndex: number) => void;

function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function clampRadius(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Centred moving average over the measured path, which is the whole of the
 * "Path Smoothing" control. `radius` counts frames on each side, so 0 returns
 * the measured path untouched. Positions only: the tracker reports translation.
 */
function smoothKeyframePositions(
  keyframes: TrackingKeyframe[],
  radius: number,
): TrackingKeyframe[] {
  const window = Math.max(0, Math.floor(radius));
  if (window === 0 || keyframes.length === 0) {
    return keyframes;
  }

  return keyframes.map((keyframe, index) => {
    const from = Math.max(0, index - window);
    const to = Math.min(keyframes.length - 1, index + window);
    let sumX = 0;
    let sumY = 0;
    for (let i = from; i <= to; i++) {
      sumX += keyframes[i].position.x;
      sumY += keyframes[i].position.y;
    }
    const count = to - from + 1;
    return {
      ...keyframe,
      position: { x: sumX / count, y: sumY / count },
    };
  });
}

export class MotionTrackingEngine {
  private trackingJobs: Map<string, TrackingJob> = new Map();
  private trackingData: Map<string, TrackingData> = new Map();
  private attachments: Map<string, TrackingAttachment> = new Map();
  private progressCallbacks: Set<TrackingProgressCallback> = new Set();
  private lostCallbacks: Set<TrackingLostCallback> = new Set();

  constructor() {}
  // Tracking Operations
  async startTracking(
    clipId: string,
    region: Rectangle,
    frameSource: TrackingFrameSource,
    options: TrackingOptions,
  ): Promise<TrackingJob> {
    if (!(region.width > 0) || !(region.height > 0)) {
      throw new Error("Tracking region must have a positive width and height.");
    }
    if (!(options.frameRate > 0)) {
      throw new Error("Tracking needs the source frame rate.");
    }
    if (options.endFrame <= options.startFrame) {
      throw new Error("Tracking range must end after it starts.");
    }

    const jobId = generateId("track-job");
    const job: TrackingJob = {
      id: jobId,
      clipId,
      region,
      status: "pending",
      progress: 0,
      options: {
        frameRate: options.frameRate,
        startFrame: options.startFrame,
        endFrame: options.endFrame,
        confidenceThreshold:
          options.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD,
        smoothing: options.smoothing ?? 0,
      },
      startTime: Date.now(),
    };

    this.trackingJobs.set(jobId, job);
    void this.runTracking(job, frameSource);

    return job;
  }

  private async runTracking(
    job: TrackingJob,
    frameSource: TrackingFrameSource,
  ): Promise<void> {
    job.status = "running";
    this.trackingJobs.set(job.id, job);

    const { startFrame, endFrame, frameRate, confidenceThreshold, smoothing } =
      job.options;
    const totalFrames = endFrame - startFrame;
    const threshold = confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD;
    const scale = frameSource.scale > 0 ? frameSource.scale : 1;

    const keyframes: TrackingKeyframe[] = [];
    const confidence: number[] = [];
    const lostFrames: number[] = [];

    const regionSpan = Math.min(job.region.width, job.region.height) * scale;
    const blockRadius = clampRadius(
      Math.round(regionSpan / 2),
      2,
      MAX_BLOCK_RADIUS,
    );
    const searchRadius = clampRadius(blockRadius * 2, 4, MAX_SEARCH_RADIUS);

    let previousFrame = await frameSource.getFrame(startFrame);
    if (!previousFrame) {
      this.failJob(job, `Frame ${startFrame} could not be decoded.`);
      return;
    }

    // The first sample is the region the user placed rather than a measurement,
    // so it is recorded at full confidence by definition.
    let analysisPosition: Point = {
      x: (job.region.x + job.region.width / 2) * scale,
      y: (job.region.y + job.region.height / 2) * scale,
    };
    keyframes.push({
      frame: startFrame,
      position: {
        x: analysisPosition.x / scale,
        y: analysisPosition.y / scale,
      },
    });
    confidence.push(1);

    for (let i = 1; i <= totalFrames; i++) {
      if (this.isCancelled(job.id)) {
        return;
      }

      const frame = startFrame + i;
      const nextFrame = await frameSource.getFrame(frame);
      if (!nextFrame) {
        this.failJob(job, `Frame ${frame} could not be decoded.`);
        return;
      }

      const sample = trackOpticalFlowStep(
        previousFrame,
        nextFrame,
        analysisPosition,
        { blockRadius, searchRadius },
      );
      analysisPosition = { x: sample.x, y: sample.y };
      previousFrame = nextFrame;

      // Block matching measures translation only, so scale and rotation are
      // left unset rather than guessed. Only ingested or manually corrected
      // keyframes carry them.
      keyframes.push({
        frame,
        position: {
          x: analysisPosition.x / scale,
          y: analysisPosition.y / scale,
        },
      });

      confidence.push(sample.confidence);
      if (sample.confidence < threshold) {
        lostFrames.push(frame);
        this.notifyTrackingLost(frame);
      }
      job.progress = (i / totalFrames) * 100;
      this.trackingJobs.set(job.id, job);
      this.notifyProgress(job.progress);
    }

    if (this.isCancelled(job.id)) {
      return;
    }

    const trackId = generateId("track");
    const trackingData: TrackingData = {
      trackId,
      clipId: job.clipId,
      keyframes: smoothKeyframePositions(keyframes, smoothing ?? 0),
      confidence,
      lostFrames,
      region: job.region,
      frameRate,
    };

    this.trackingData.set(trackId, trackingData);

    // Complete job
    job.status = "completed";
    job.progress = 100;
    job.endTime = Date.now();
    this.trackingJobs.set(job.id, job);
  }

  private isCancelled(jobId: string): boolean {
    const current = this.trackingJobs.get(jobId);
    return !current || current.status === "cancelled";
  }

  private failJob(job: TrackingJob, message: string): void {
    job.status = "failed";
    job.error = message;
    job.endTime = Date.now();
    this.trackingJobs.set(job.id, job);
  }

  cancelTracking(jobId: string): void {
    const job = this.trackingJobs.get(jobId);
    if (job && job.status === "running") {
      job.status = "cancelled";
      job.endTime = Date.now();
      this.trackingJobs.set(jobId, job);
    }
  }

  getTrackingJob(jobId: string): TrackingJob | undefined {
    return this.trackingJobs.get(jobId);
  }

  getTrackingData(clipId: string, trackId: string): TrackingData | undefined {
    const data = this.trackingData.get(trackId);
    if (data && data.clipId === clipId) {
      return data;
    }
    return undefined;
  }

  getTrackingDataForClip(clipId: string): TrackingData[] {
    return Array.from(this.trackingData.values()).filter(
      (d) => d.clipId === clipId,
    );
  }

  ingestTrackingData(
    clipId: string,
    keyframes: TrackingKeyframe[],
    region: Rectangle,
    frameRate: number,
  ): TrackingData {
    const trackId = generateId("track");
    const trackingData: TrackingData = {
      trackId,
      clipId,
      keyframes,
      confidence: keyframes.map(() => 1),
      lostFrames: [],
      region,
      frameRate: frameRate > 0 ? frameRate : 30,
    };
    this.trackingData.set(trackId, trackingData);
    return trackingData;
  }
  // Tracking Application (Requirement 23.3)
  applyTrackingToElement(
    trackId: string,
    elementId: string,
    offset: Point = { x: 0, y: 0 },
  ): void {
    const trackingData = this.trackingData.get(trackId);
    if (!trackingData) {
      throw new Error(`Tracking data not found: ${trackId}`);
    }

    const attachment: TrackingAttachment = {
      elementId,
      trackId,
      offset,
      applyScale: true,
      applyRotation: true,
    };

    this.attachments.set(elementId, attachment);
  }

  removeTrackingFromElement(elementId: string): void {
    this.attachments.delete(elementId);
  }

  getAttachment(elementId: string): TrackingAttachment | undefined {
    return this.attachments.get(elementId);
  }

  getAttachmentsForTrack(trackId: string): TrackingAttachment[] {
    return Array.from(this.attachments.values()).filter(
      (a) => a.trackId === trackId,
    );
  }

  getElementPositionAtTime(elementId: string, time: number): Point | null {
    const attachment = this.attachments.get(elementId);
    if (!attachment) {
      return null;
    }

    const trackingData = this.trackingData.get(attachment.trackId);
    if (!trackingData) {
      return null;
    }
    const frame = Math.floor(time * trackingData.frameRate);
    const trackedPosition = this.getTrackedPositionAtFrame(trackingData, frame);
    if (!trackedPosition) {
      return null;
    }
    return {
      x: trackedPosition.x + attachment.offset.x,
      y: trackedPosition.y + attachment.offset.y,
    };
  }

  private getTrackedPositionAtFrame(
    data: TrackingData,
    frame: number,
  ): Point | null {
    if (data.keyframes.length === 0) {
      return null;
    }
    let prevKeyframe: TrackingKeyframe | null = null;
    let nextKeyframe: TrackingKeyframe | null = null;

    for (const kf of data.keyframes) {
      if (kf.frame <= frame) {
        prevKeyframe = kf;
      } else if (!nextKeyframe) {
        nextKeyframe = kf;
        break;
      }
    }
    if (!prevKeyframe) {
      return data.keyframes[0].position;
    }
    if (!nextKeyframe || prevKeyframe.frame === frame) {
      return prevKeyframe.position;
    }
    const t =
      (frame - prevKeyframe.frame) / (nextKeyframe.frame - prevKeyframe.frame);

    return {
      x:
        prevKeyframe.position.x +
        (nextKeyframe.position.x - prevKeyframe.position.x) * t,
      y:
        prevKeyframe.position.y +
        (nextKeyframe.position.y - prevKeyframe.position.y) * t,
    };
  }
  // Tracking Lost Notification (Requirement 23.4)
  onTrackingProgress(callback: TrackingProgressCallback): () => void {
    this.progressCallbacks.add(callback);
    return () => this.progressCallbacks.delete(callback);
  }

  onTrackingLost(callback: TrackingLostCallback): () => void {
    this.lostCallbacks.add(callback);
    return () => this.lostCallbacks.delete(callback);
  }

  private notifyProgress(progress: number): void {
    for (const callback of this.progressCallbacks) {
      callback(progress);
    }
  }

  private notifyTrackingLost(frameIndex: number): void {
    for (const callback of this.lostCallbacks) {
      callback(frameIndex);
    }
  }
  // Manual Correction (Requirement 23.4)
  correctTrackingPoint(
    trackId: string,
    frameIndex: number,
    position: Point,
  ): void {
    const data = this.trackingData.get(trackId);
    if (!data) {
      throw new Error(`Tracking data not found: ${trackId}`);
    }
    const keyframeIndex = data.keyframes.findIndex(
      (kf) => kf.frame === frameIndex,
    );

    if (keyframeIndex >= 0) {
      data.keyframes[keyframeIndex].position = position;
      data.confidence[keyframeIndex] = 1.0;
      const lostIndex = data.lostFrames.indexOf(frameIndex);
      if (lostIndex >= 0) {
        data.lostFrames.splice(lostIndex, 1);
      }
    } else {
      const newKeyframe: TrackingKeyframe = {
        frame: frameIndex,
        position,
        scale: 1,
        rotation: 0,
      };
      let insertIndex = data.keyframes.findIndex((kf) => kf.frame > frameIndex);
      if (insertIndex === -1) {
        insertIndex = data.keyframes.length;
      }

      data.keyframes.splice(insertIndex, 0, newKeyframe);
      data.confidence.splice(insertIndex, 0, 1.0);
    }

    this.trackingData.set(trackId, data);
  }
  // Offset Management (Requirement 23.5)
  setTrackingOffset(elementId: string, offset: Point): void {
    const attachment = this.attachments.get(elementId);
    if (attachment) {
      attachment.offset = offset;
      this.attachments.set(elementId, attachment);
    }
  }

  getTrackingOffset(elementId: string): Point | null {
    const attachment = this.attachments.get(elementId);
    return attachment?.offset ?? null;
  }

  setApplyScale(elementId: string, applyScale: boolean): void {
    const attachment = this.attachments.get(elementId);
    if (attachment) {
      attachment.applyScale = applyScale;
      this.attachments.set(elementId, attachment);
    }
  }

  setApplyRotation(elementId: string, applyRotation: boolean): void {
    const attachment = this.attachments.get(elementId);
    if (attachment) {
      attachment.applyRotation = applyRotation;
      this.attachments.set(elementId, attachment);
    }
  }
  deleteTrackingData(trackId: string): void {
    for (const [elementId, attachment] of this.attachments) {
      if (attachment.trackId === trackId) {
        this.attachments.delete(elementId);
      }
    }
    this.trackingData.delete(trackId);
  }

  deleteTrackingDataForClip(clipId: string): void {
    for (const [trackId, data] of this.trackingData) {
      if (data.clipId === clipId) {
        this.deleteTrackingData(trackId);
      }
    }
  }

  clear(): void {
    this.trackingJobs.clear();
    this.trackingData.clear();
    this.attachments.clear();
  }

  getTrackIds(): string[] {
    return Array.from(this.trackingData.keys());
  }

  hasTracking(elementId: string): boolean {
    return this.attachments.has(elementId);
  }
}
let motionTrackingEngineInstance: MotionTrackingEngine | null = null;

export function getMotionTrackingEngine(): MotionTrackingEngine {
  if (!motionTrackingEngineInstance) {
    motionTrackingEngineInstance = new MotionTrackingEngine();
  }
  return motionTrackingEngineInstance;
}

export function initializeMotionTrackingEngine(): MotionTrackingEngine {
  motionTrackingEngineInstance = new MotionTrackingEngine();
  return motionTrackingEngineInstance;
}
